import { useCallback, useMemo, useState } from 'react';

import { HOURS_QUANTUM, snapToQuantum } from '../common';
import { type WorkItem, workItemTypeAccent } from '../work-items';
import { usePending } from '../worklog';
import type { HoursPanelTarget } from './HoursPanel';

const DEFAULT_HOURS_INPUT = '0.25';
const MAX_DAILY_HOURS = 24;

export type UseQuickAdd = {
    selected: WorkItem | null;
    hoursPanelTarget: HoursPanelTarget | null;
    hoursInput: string;
    setHoursInput: (next: string) => void;
    currentHours: number;
    remaining: number;
    canAdd: boolean;
    handleSelectWorkItem: (item: WorkItem) => void;
    handleCloseEntry: () => void;
    handleAdd: () => void;
    reset: () => void;
};

export const useQuickAdd = (date: string, savedHours: number): UseQuickAdd => {
    const { totalForDate, add } = usePending();

    const [selected, setSelected] = useState<WorkItem | null>(null);
    const [hoursInput, setHoursInput] = useState(DEFAULT_HOURS_INPUT);

    const pendingHours = totalForDate(date);
    const remaining = Math.max(0, MAX_DAILY_HOURS - savedHours - pendingHours);

    const parsedHours = useMemo(() => {
        const v = Number.parseFloat(hoursInput);
        return Number.isFinite(v) && v > 0 ? v : null;
    }, [hoursInput]);

    const currentHours = parsedHours ?? HOURS_QUANTUM;
    const canAdd = selected !== null && remaining > 0 && parsedHours !== null && parsedHours <= remaining;

    const handleSelectWorkItem = useCallback((item: WorkItem) => {
        setSelected(item);
        setHoursInput(DEFAULT_HOURS_INPUT);
    }, []);

    const handleCloseEntry = useCallback(() => {
        setSelected(null);
        setHoursInput(DEFAULT_HOURS_INPUT);
    }, []);

    const handleAdd = useCallback(() => {
        if (!selected || parsedHours === null) {
            return;
        }
        add({
            workItemId: selected.id,
            workItemTitle: selected.title,
            workItemType: selected.workItemType,
            date,
            hours: snapToQuantum(parsedHours),
        });
        setSelected(null);
        setHoursInput(DEFAULT_HOURS_INPUT);
    }, [selected, parsedHours, add, date]);

    const reset = useCallback(() => {
        setSelected(null);
        setHoursInput(DEFAULT_HOURS_INPUT);
    }, []);

    const hoursPanelTarget = useMemo<HoursPanelTarget | null>(
        () =>
            selected
                ? {
                      id: selected.id,
                      title: selected.title,
                      accent: workItemTypeAccent(selected.workItemType, selected.title),
                      typeLabel: selected.workItemType,
                  }
                : null,
        [selected],
    );

    return {
        selected,
        hoursPanelTarget,
        hoursInput,
        setHoursInput,
        currentHours,
        remaining,
        canAdd,
        handleSelectWorkItem,
        handleCloseEntry,
        handleAdd,
        reset,
    };
};
